<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useCompaniesStore } from 'dashboard/stores/companies';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAdmin } from 'dashboard/composables/useAdmin';
import ContactAPI from 'dashboard/api/contacts';
import { debounce } from '@chatwoot/utils';

import Button from 'dashboard/components-next/button/Button.vue';
import SelectMenu from 'dashboard/components-next/selectmenu/SelectMenu.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import { TASK_POPOVER_PANEL_CLASS } from '../../taskPopoverPanelClass';

const props = defineProps({
  filters: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['apply', 'clear']);

const { t } = useI18n();
const store = useStore();
const companiesStore = useCompaniesStore();
const { isAdmin } = useAdmin();
const agents = useMapGetter('agents/getVerifiedAgents');

const isOpen = ref(false);
const status = ref(props.filters.status || '');
const companyId = ref(props.filters.companyId || '');
const contactId = ref(props.filters.contactId || '');
const contactName = ref(props.filters.contactName || '');
const dueFrom = ref(props.filters.dueFrom || '');
const dueTo = ref(props.filters.dueTo || '');
// 'all' | 'mine' | '<agentId>' — only meaningful for administrators, since
// agents are already scoped to their own tasks by the backend.
const owner = ref(props.filters.owner || 'all');

const contactResults = ref([]);
const contactSearchValue = ref('');

const statusOptions = computed(() => [
  { value: '', label: t('TASKS_DASHBOARD.FILTER.STATUS_ALL') },
  {
    value: 'todo',
    label: t('CONTACTS_LAYOUT.SIDEBAR.TASKS.STATUS.TODO'),
  },
  {
    value: 'in_progress',
    label: t('CONTACTS_LAYOUT.SIDEBAR.TASKS.STATUS.IN_PROGRESS'),
  },
  {
    value: 'waiting_customer',
    label: t('CONTACTS_LAYOUT.SIDEBAR.TASKS.STATUS.WAITING_CUSTOMER'),
  },
  {
    value: 'done',
    label: t('CONTACTS_LAYOUT.SIDEBAR.TASKS.STATUS.DONE'),
  },
]);

const statusLabel = computed(
  () =>
    statusOptions.value.find(option => option.value === status.value)
      ?.label || t('TASKS_DASHBOARD.FILTER.STATUS')
);

const ownerOptions = computed(() => [
  { value: 'all', label: t('TASKS_DASHBOARD.FILTER.OWNER_ALL') },
  { value: 'mine', label: t('TASKS_DASHBOARD.FILTER.OWNER_MINE') },
  ...(agents.value || []).map(agent => ({
    value: String(agent.id),
    label: agent.name,
  })),
]);

const ownerLabel = computed(
  () =>
    ownerOptions.value.find(option => option.value === owner.value)?.label ||
    t('TASKS_DASHBOARD.FILTER.OWNER')
);

const companyOptions = computed(() => [
  { value: '', label: t('TASKS_DASHBOARD.FILTER.COMPANY_ALL') },
  ...(companiesStore.getCompaniesList || []).map(company => ({
    value: String(company.id),
    label: company.name,
  })),
]);

const companyLabel = computed(
  () =>
    companyOptions.value.find(option => option.value === String(companyId.value))
      ?.label || t('TASKS_DASHBOARD.FILTER.COMPANY')
);

const searchContacts = debounce(async query => {
  if (!query) {
    contactResults.value = [];
    return;
  }
  const { data } = await ContactAPI.search(query);
  contactResults.value = data.payload || [];
}, 300);

const onContactSearchInput = event => {
  contactSearchValue.value = event.target.value;
  searchContacts(contactSearchValue.value);
};

const onSelectContact = contact => {
  contactId.value = contact.id;
  contactName.value = contact.name;
  contactResults.value = [];
  contactSearchValue.value = '';
};

const clearContact = () => {
  contactId.value = '';
  contactName.value = '';
};

const toggleOpen = () => {
  isOpen.value = !isOpen.value;
};

const applyFilters = () => {
  emit('apply', {
    status: status.value,
    companyId: companyId.value,
    contactId: contactId.value,
    contactName: contactName.value,
    dueFrom: dueFrom.value,
    dueTo: dueTo.value,
    owner: owner.value,
  });
  isOpen.value = false;
};

const clearFilters = () => {
  status.value = '';
  companyId.value = '';
  contactId.value = '';
  contactName.value = '';
  dueFrom.value = '';
  dueTo.value = '';
  owner.value = 'all';
  emit('clear');
  isOpen.value = false;
};

const hasActiveFilters = computed(
  () =>
    !!(
      props.filters.status ||
      props.filters.companyId ||
      props.filters.contactId ||
      props.filters.dueFrom ||
      props.filters.dueTo ||
      (props.filters.owner && props.filters.owner !== 'all')
    )
);

onMounted(() => {
  if (!companiesStore.getCompaniesList?.length) {
    companiesStore.get({ page: 1, sort: 'name' });
  }
  if (isAdmin.value && !agents.value?.length) {
    store.dispatch('agents/get');
  }
});
</script>

<template>
  <div class="relative">
    <Button
      icon="i-lucide-filter"
      color="slate"
      size="sm"
      variant="ghost"
      :class="isOpen || hasActiveFilters ? 'bg-n-alpha-2' : ''"
      @click="toggleOpen"
    />
    <div
      v-if="isOpen"
      v-on-clickaway="() => (isOpen = false)"
      class="absolute top-full mt-1 ltr:right-0 rtl:left-0 flex flex-col gap-4 w-80 p-4 z-20"
      :class="TASK_POPOVER_PANEL_CLASS"
    >
      <div v-if="isAdmin" class="flex items-center justify-between gap-2">
        <span class="text-sm text-n-slate-12">
          {{ t('TASKS_DASHBOARD.FILTER.OWNER') }}
        </span>
        <SelectMenu
          v-model="owner"
          :options="ownerOptions"
          :label="ownerLabel"
        />
      </div>
      <div class="flex items-center justify-between gap-2">
        <span class="text-sm text-n-slate-12">
          {{ t('TASKS_DASHBOARD.FILTER.STATUS') }}
        </span>
        <SelectMenu
          v-model="status"
          :options="statusOptions"
          :label="statusLabel"
        />
      </div>
      <div class="flex items-center justify-between gap-2">
        <span class="text-sm text-n-slate-12">
          {{ t('TASKS_DASHBOARD.FILTER.COMPANY') }}
        </span>
        <SelectMenu
          v-model="companyId"
          :options="companyOptions"
          :label="companyLabel"
        />
      </div>
      <div class="flex flex-col gap-1">
        <span class="text-sm text-n-slate-12">
          {{ t('TASKS_DASHBOARD.FILTER.CONTACT') }}
        </span>
        <div v-if="contactName" class="flex items-center justify-between">
          <span class="text-sm text-n-slate-11 truncate">
            {{ contactName }}
          </span>
          <Button
            icon="i-lucide-x"
            size="xs"
            variant="ghost"
            color="slate"
            @click="clearContact"
          />
        </div>
        <div v-else class="relative">
          <Input
            :model-value="contactSearchValue"
            type="search"
            :placeholder="t('TASKS_DASHBOARD.FILTER.CONTACT_PLACEHOLDER')"
            class="w-full"
            @input="onContactSearchInput"
          />
          <div
            v-if="contactResults.length"
            class="absolute top-full mt-1 w-full flex flex-col gap-0.5 bg-n-alpha-3 backdrop-blur-[100px] border border-n-weak rounded-lg p-1 z-30 max-h-40 overflow-y-auto"
          >
            <button
              v-for="contact in contactResults"
              :key="contact.id"
              type="button"
              class="text-left text-sm px-2 py-1 rounded-md hover:bg-n-alpha-2 text-n-slate-12"
              @click="onSelectContact(contact)"
            >
              {{ contact.name }}
            </button>
          </div>
        </div>
      </div>
      <div class="flex items-center gap-2">
        <div class="flex flex-col gap-1 flex-1">
          <span class="text-sm text-n-slate-12">
            {{ t('TASKS_DASHBOARD.FILTER.DUE_FROM') }}
          </span>
          <input
            v-model="dueFrom"
            type="date"
            class="w-full rounded-lg border border-n-weak bg-n-alpha-1 px-2 py-1 text-sm text-n-slate-12"
          />
        </div>
        <div class="flex flex-col gap-1 flex-1">
          <span class="text-sm text-n-slate-12">
            {{ t('TASKS_DASHBOARD.FILTER.DUE_TO') }}
          </span>
          <input
            v-model="dueTo"
            type="date"
            class="w-full rounded-lg border border-n-weak bg-n-alpha-1 px-2 py-1 text-sm text-n-slate-12"
          />
        </div>
      </div>
      <div class="flex items-center justify-between gap-2">
        <Button
          size="sm"
          variant="ghost"
          color="slate"
          :label="t('TASKS_DASHBOARD.FILTER.CLEAR')"
          @click="clearFilters"
        />
        <Button
          size="sm"
          solid
          blue
          :label="t('TASKS_DASHBOARD.FILTER.APPLY')"
          @click="applyFilters"
        />
      </div>
    </div>
  </div>
</template>
